import { lazy } from 'react';
import type { ComponentType, ReactElement } from 'react';
import {
  PUBLIC_ROUTE_MANIFEST,
  type PublicChunkName,
  type RouteManifestEntry,
} from './RouteManifest';

export type PublicRouteConfigEntry = {
  path: string;
  element: ReactElement;
  skipProfileFetchPath?: boolean;
};

/**
 * Public (unprotected) routes. lazy() strings must be static literals — Vite cannot
 * split dynamic import() arguments. Keep in sync with sourcePath in RouteManifest.ts.
 *
 * TODO: route-landing is 414 KB raw / 270 KB gzip. Run `ANALYZE=true npm run build`
 * to find heavy deps, then split or lazy-load sub-sections.
 */

// import() strings must be static literals — Vite cannot analyze dynamic paths.
// Keep each string in sync with the matching sourcePath in RouteManifest.ts.
const componentMap: Record<PublicChunkName, ComponentType> = {
  'route-landing': lazy(() => import('app/pages/LandingPage')),
  'route-get-in-touch': lazy(() => import('app/pages/LandingPage/GetInTouch')),
  'route-agent-login': lazy(
    () => import('app/pages/Agent/Login/AgentLoginPage')
  ),
  'route-compliance-login': lazy(() => import('app/pages/Compliance/Login')),
  'route-agent-onboarding-create-account': lazy(
    () => import('app/pages/Agent/Onboarding/CreateAccountPage')
  ),
  'route-agent-onboarding-add-email': lazy(
    () => import('app/pages/Agent/Onboarding/AddEmailPage')
  ),
  'route-email-labels-auth': lazy(
    () => import('app/pages/Agent/Onboarding/EmailLabelsAuthPage')
  ),
  'route-agent-onboarding-completed': lazy(
    () => import('app/pages/Agent/Onboarding/CompletedPage')
  ),
  'route-signature': lazy(() => import('app/pages/SignatureRoute')),
  'route-privacy-policy': lazy(() => import('app/pages/Shared/PrivacyPolicy')),
  'route-terms-and-conditions': lazy(
    () => import('app/pages/Shared/TermsAndConditions')
  ),
  'route-careers': lazy(() => import('app/pages/CareersPage')),
  'route-confirmation': lazy(
    () => import('app/pages/Confirmation/ConfirmationScreen')
  ),
  'route-new-listing': lazy(() => import('app/pages/NewListing')),
  'route-sales-form': lazy(() => import('app/pages/SalesForm')),
  'route-deal-sheet': lazy(() => import('app/pages/DealSheet')),
  'route-lease-deal-sheet': lazy(() => import('app/pages/LeaseDealSheet')),
  'route-entity-redirect': lazy(
    () => import('app/components/common/EntityRedirect')
  ),
  'route-agent-upload': lazy(
    () => import('app/pages/AgentUpload/AgentUploadPage')
  ),
  'route-calendar-link': lazy(
    () => import('app/pages/CalendarLink/CalendarLinkPage')
  ),
  'route-bw-document-view': lazy(() => import('app/pages/BWDocumentView')),
};

export const PUBLIC_ROUTES_CONFIG: PublicRouteConfigEntry[] =
  PUBLIC_ROUTE_MANIFEST.map((entry) => {
    const Component = componentMap[entry.chunkName];
    return {
      path: entry.routePath,
      element: <Component />,
      skipProfileFetchPath: (entry as RouteManifestEntry).skipProfileFetchPath,
    };
  });
